import { formatMoney } from '@web-platform/shared-universal-utils-frontend/src/helpers/formatMoney';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { mapUpdateStatus, priceUpdateStatusMap } from './sonarV3UpdateStatusMapper';

import type {
  SkyscannerSonarCommonV1Carrier,
  SkyscannerSonarCommonV1Enums,
  SkyscannerSonarV3alpha1Response,
  SkyscannerSonarV3Response,
} from '@skyscanner-internal/sonar-web-http-client';
import type { PricingItemBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/flights/itinerariesBookingPanel';

type Carrier = SkyscannerSonarCommonV1Carrier.Carrier;

const TRANSFER_PROTECTION_STATUSES = {
  TRANSFER_PROTECTION_UNSPECIFIED: '',
  TRANSFER_PROTECTION_NON_PROTECTED: 'self-transfer',
  TRANSFER_PROTECTION_PROTECTED: 'protected-self-transfer',
} as const;

const mapSonarV3TransferProtection = (
  transferProtection: SkyscannerSonarCommonV1Enums.TransferProtection | undefined,
): string | undefined => transferProtection && TRANSFER_PROTECTION_STATUSES[transferProtection];

export const mapSonarV3PricingItems = (
  pricingItems:
    | (SkyscannerSonarV3Response.PricingItem[] & SkyscannerSonarV3alpha1Response.PricingItem[])
    | undefined,
): PricingItemBookingPanel[] | undefined =>
  pricingItems?.reduce<PricingItemBookingPanel[]>(
    (
      items,
      item: SkyscannerSonarV3Response.PricingItem & SkyscannerSonarV3alpha1Response.PricingItem,
    ) => {
      try {
        const { marketingCarriers, marketingCarriersIds } = item;
        return items.concat({
          agent_id: item.agent?.id || '',
          price: {
            amount: item.price ? formatMoney(item.price, false) : 0,
            update_status: mapUpdateStatus(priceUpdateStatusMap, item.updateStatus),
          },
          segment_ids: item.segmentIds || [],
          transfer_protection: mapSonarV3TransferProtection(item.transferProtection),
          url: item.uri ?? '',
          deal_message_key: item.dealMessageKey || '',
          ...(marketingCarriersIds && marketingCarriersIds.length > 0
            ? { marketing_carriers_ids: marketingCarriersIds }
            : {}),
          ...(marketingCarriers && marketingCarriers.length > 0
            ? {
                marketing_carriers: marketingCarriers.map((carrier: Carrier) => ({
                  id: carrier.id ?? 0,
                  name: carrier.name ?? '',
                  alt_id: carrier.altId ?? '',
                  display_code: carrier.displayCode ?? '',
                })),
              }
            : {}),
        });
      } catch (error) {
        logError({
          component: 'mapSonarV3PricingItems',
          data: JSON.stringify(item),
          error,
          message: `SONAR_V3: ${error.message}`,
        });
        return items;
      }
    },
    [],
  );
